import { useCreateDialogRequest, useOpenedFromCreateDialog } from 'components/create-dialog/CreateDialogContext';
import { CSSProperties, RefObject, useCallback, useContext } from 'react';
import { useDispatch } from 'store';
import { disableButtons } from 'store/reducers/buttons';
import { ModalBehaviour, ModalClassNames, ModalContext } from './ModalProvider';

export interface ModalActions {
    closeModal: () => void;
    openModal: (
        atrContent: JSX.Element,
        atrOnCancel?: () => void,
        atrModalStyle?: CSSProperties,
        atrContainer?: RefObject<HTMLElement | null>,
        atrClassNames?: ModalClassNames,
        atrBehaviour?: ModalBehaviour
    ) => void;
}

export const useModal = (): ModalActions => {
    const { setModalStack } = useContext(ModalContext);
    const dispatch = useDispatch();
    // The caller is in the create dialog, or in a popup that was opened from it. The dialog is asked as the
    // popup opens: a form opens its follow-up popups once it has finished, and those are opened from a page,
    // unless the dialog stayed open for another creation.
    const isCreateDialogOpen = useCreateDialogRequest()?.isOpen;
    const openedFromCreateDialog = useOpenedFromCreateDialog();

    const closeModal = useCallback((): void => {
        setModalStack((prevStack) => {
            const newStack = prevStack.slice(0, -1);
            // Only disable buttons when all modals are closed
            if (newStack.length === 0) {
                dispatch(disableButtons(false));
            }
            return newStack;
        });
    }, [dispatch, setModalStack]);

    const openModal = useCallback(
        (
            atrContent: JSX.Element,
            atrOnCancel?: () => void,
            atrModalStyle?: CSSProperties,
            atrContainer?: RefObject<HTMLElement | null>,
            atrClassNames?: ModalClassNames,
            atrBehaviour?: ModalBehaviour
        ): void => {
            const fromCreateDialog = openedFromCreateDialog || isCreateDialogOpen?.() === true;
            setModalStack((prevStack) => [
                ...prevStack,
                {
                    content: atrContent,
                    onCancel: atrOnCancel || closeModal,
                    modalStyle: atrModalStyle,
                    container: atrContainer,
                    fromCreateDialog,
                    classNames: atrClassNames,
                    ...atrBehaviour,
                },
            ]);
        },
        [closeModal, setModalStack, openedFromCreateDialog, isCreateDialogOpen]
    );

    return {
        closeModal,
        openModal,
    };
};
